import { useEffect, useLayoutEffect, useState, useDeferredValue, useCallback } from 'react';
import { motion, useDragControls, useAnimationControls } from 'framer-motion';
import PropTypes from "prop-types";
import clsx from "clsx";
import { useClickAway, useMediaQuery } from "@educaplay/core/hooks";
import classes from "./DialogClue.module.scss";
import { IconClueInformationModal } from '../../icons';

const MIN_SWIPE_DISTANCE = 40;
const MIN_SWIPE_DISTANCE_Y = 7;
const FLAP_WIDTH = 32;

export default function SwipeableDrawer(props) {
    const { open, onClose, onOpen, children, className } = props;
    const [isDragging, setIsDragging] = useState(false);
    const isDeferredDragging = useDeferredValue(isDragging);
    const isDesktop = useMediaQuery("(orientation: landscape)")
    const [distance, setDistance] = useState(isDesktop ? -280 : 280);
    const [dragConstraints, setDragConstraints] = useState(null);
    const direction = isDesktop ? "x" : "y";
    const directionNotUsed =  isDesktop ? "y" : "x";

    const dragControls = useDragControls();
    const animationControls = useAnimationControls();

    const drawerRef = useClickAway(() => {
        if (open) {
            handleClose()
        }
    });

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => {
                if (!drawerRef.current) return;
                const { width, height } = drawerRef.current.getBoundingClientRect();
                const dialogWithoutFlap = width - FLAP_WIDTH;
                const dialogWithoutFlapHeight = height - FLAP_WIDTH;

                setDistance(isDesktop ? -dialogWithoutFlap : dialogWithoutFlapHeight);
                setDragConstraints(isDesktop ? { right: 0, left: -dialogWithoutFlap } : { top: 0, bottom: dialogWithoutFlapHeight});
            });
        })

        resizeObserver.observe(drawerRef.current);

        return () => {
            resizeObserver.disconnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesktop])

    useLayoutEffect(() => {
        animationControls.set({ [direction]: distance });
        animationControls.set({ [directionNotUsed]: 0 });
    }, [distance, animationControls, direction, directionNotUsed])

    const animateClose = useCallback(() => {
        animationControls.start({ [direction]: distance, transition: { duration: 0.3, ease: "easeOut" } });
    }, [animationControls, direction, distance])

    const animateOpen = useCallback(() => {
        animationControls.start({ [direction]: 0, transition: { duration: 0.2, ease: "easeOut" } });
    }, [animationControls, direction])

    useEffect(() => {
        const action = open ? animateOpen : animateClose;
        action();
    }, [open, animateOpen, animateClose])

    const handleClose = () => onClose()
    const handleOpen = () => onOpen();

    const handleDragEndClose = (_event, info) => {
        if (isDesktop && info.offset.x < -MIN_SWIPE_DISTANCE) {
            handleClose();
        } else if (!isDesktop && info.offset.y > MIN_SWIPE_DISTANCE_Y) {
            handleClose();
        } else {
            animateOpen();
        }
    }

    const handleDragEndOpen = (_event, info) => {
        if (isDesktop && info.offset.x > MIN_SWIPE_DISTANCE) {
            handleOpen();
        } else if (!isDesktop && info.offset.y < -MIN_SWIPE_DISTANCE_Y) {
            handleOpen();
        } else {
            animateClose();
        }
    }

    const handleDragEnd = (...params) => {
        setIsDragging(false);
        const action = open ? handleDragEndClose : handleDragEndOpen;
        action(...params);
    }

    const handleClick = () => {
        if (isDeferredDragging) return;

        const action = open ? handleClose : handleOpen;
        action();
    }

    const handleDragStart = (e) => {
        dragControls.start(e);
    }

    return (
        <motion.div
            dragListener={isDesktop}
            ref={drawerRef}
            drag={direction}
            className={clsx(classes.drawerBox, className)}
            dragConstraints={dragConstraints}
            dragControls={dragControls}
            dragElastic={0.01}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            dragTransition={{ bounceDamping: 30, bounceStiffness: 800 }}
            animate={animationControls}
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
            <div className={classes.swipeContainer} onPointerDown={handleDragStart}>
                <div className={classes.informationicon}>
                    <IconClueInformationModal />
                </div>
                <div className={classes.swipe} onClick={handleClick} />
            </div>

            <div className={clsx(classes.content)}>
                {children}
            </div>
        </motion.div>
    )
}

SwipeableDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
    className: PropTypes.string,
}
